// /src/pages/Login.js
import { useState } from "react";
import { Eye, EyeOff, Loader } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetSent, setResetSent] = useState(false);

  // LOGIN
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error || "Login failed. Please try again.");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      window.location.href = "/";
    } catch (err) {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  };

  // REQUEST RESET — now just shows "check your email"
  const handleRequestReset = async () => {
    if (!resetEmail.trim()) {
      setResetMsg("Please enter your email address.");
      return;
    }

    setResetLoading(true);
    setResetMsg("");

    try {
      const res = await fetch("/api/requestpasswordreset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Show the actual server error so we can debug
        setResetMsg(data.error || `Server error (${res.status})`);
      } else {
        setResetSent(true);
        setResetMsg("Check your inbox — if an account exists we've sent a reset link.");
      }
    } catch (err) {
      setResetMsg("Request failed. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <div style={styles.logoMark}>P</div>
          <div style={styles.logoText}>Propertunity</div>
        </div>
        <h1 style={styles.heading}>Welcome back</h1>
        <p style={styles.subheading}>Sign in to your account</p>

        {/* Login form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                style={{ ...styles.input, paddingRight: 44 }}
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={styles.eyeBtn}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div style={{ textAlign: "right", marginTop: 6 }}>
              <button
                type="button"
                onClick={() => { setShowForgot((v) => !v); setResetMsg(""); setResetSent(false); }}
                style={{ fontSize: 12, color: "#60a5fa", background: "none", border: "none", cursor: "pointer" }}
              >
                Forgot password?
              </button>
            </div>
          </div>

          {error && <div style={styles.errorBanner}>{error}</div>}

          <button
            type="submit"
            disabled={loading}
            style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Loader size={16} style={{ animation: "spin 0.8s linear infinite" }} />
                Signing in...
              </span>
            ) : "Sign in"}
          </button>
        </form>

        {/* Forgot password panel */}
        {showForgot && (
          <div style={{ marginTop: 24, borderTop: "1px solid #1f2937", paddingTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ ...styles.subheading, margin: 0 }}>Enter your email and we'll send a reset link.</p>

            {!resetSent ? (
              <>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="you@company.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
                <button
                  type="button"
                  style={{ ...styles.submitBtn, opacity: resetLoading ? 0.7 : 1 }}
                  onClick={handleRequestReset}
                  disabled={resetLoading}
                >
                  {resetLoading ? (
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      <Loader size={16} style={{ animation: "spin 0.8s linear infinite" }} />
                      Sending...
                    </span>
                  ) : "Send reset link"}
                </button>
              </>
            ) : (
              <div style={styles.successBanner}>
                📬 {resetMsg}
              </div>
            )}

            {resetMsg && !resetSent && (
              <div style={styles.errorBanner}>{resetMsg}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#0a0f1e", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'DM Sans', sans-serif" },
  card: { background: "#111827", border: "1px solid #1f2937", borderRadius: 16, padding: "48px 40px", width: "100%", maxWidth: 420, animation: "fadeUp 0.4s ease both", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" },
  logoArea: { display: "flex", alignItems: "center", gap: 10, marginBottom: 32 },
  logoMark: { width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", color: "#fff", fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" },
  logoText: { fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 18, color: "#f9fafb", letterSpacing: "-0.3px" },
  heading: { fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, color: "#f9fafb", margin: "0 0 6px", letterSpacing: "-0.5px" },
  subheading: { fontSize: 14, color: "#6b7280", margin: "0 0 32px" },
  form: { display: "flex", flexDirection: "column", gap: 20 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: "#9ca3af" },
  input: { background: "#1f2937", border: "1px solid #374151", borderRadius: 8, padding: "12px 14px", fontSize: 14, color: "#f9fafb", width: "100%", boxSizing: "border-box" },
  passwordWrapper: { position: "relative" },
  eyeBtn: { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#6b7280" },
  errorBanner: { background: "#1f0a0a", border: "1px solid #7f1d1d", color: "#f87171", borderRadius: 8, padding: "10px 14px", fontSize: 13 },
  successBanner: { background: "#0a1f0a", border: "1px solid #166534", color: "#4ade80", borderRadius: 8, padding: "10px 14px", fontSize: 13 },
  submitBtn: { background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", color: "#fff", border: "none", borderRadius: 8, padding: "13px", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 4 },
};